import ModeBadge from '@/components/console/mode-badge';
import EmptyState from '@/components/empty-state';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatBytes, formatDateTime } from '@/lib/format';
import type { Attachment, ProviderMode } from '@/types/platform';

interface FilesTabProps {
  files: Attachment[];
  bucket: { name: string | null; providerMode: ProviderMode };
}

const FilesTab = ({ files, bucket }: FilesTabProps) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex flex-wrap items-center gap-2">
        <h2>Files</h2>
        <ModeBadge mode={bucket.providerMode} />
      </CardTitle>
      <CardDescription>
        Bucket:{' '}
        {bucket.name ? <code className="text-xs break-all">{bucket.name}</code> : 'not created yet'}
        . The newest files are listed first.
      </CardDescription>
    </CardHeader>
    <CardContent>
      {files.length === 0 ? (
        <EmptyState description="No files are stored for this app yet." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Size</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Field</TableHead>
              <TableHead>Uploaded</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.id}>
                <TableCell className="font-medium">
                  {/* The file is sent or redirected to, not rendered as a page. */}
                  <a href={file.url} className="underline underline-offset-4">
                    {file.filename}
                  </a>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatBytes(file.byteSize)}
                </TableCell>
                <TableCell>{file.contentType ?? '—'}</TableCell>
                <TableCell>
                  {file.fieldKey ? <code className="text-xs">{file.fieldKey}</code> : '—'}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDateTime(file.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

export default FilesTab;
